import { cueVolume } from "./cue";
import { audioContext } from "./volume";
import { useStreamer } from "@shared/ui/streamer";

/**
 * Мелодия вызова — как в Discord: у вызванного играет мелодия, у звонящего —
 * гудки, пока ему не ответили.
 *
 * Синтезом, а не файлом: мелодия Discord — его собственная, а готового звонка
 * под подходящей лицензией в наборах, откуда взяты указания (`public/sounds/`),
 * нет. Пара фраз на синусе с быстрым затуханием звучит колокольчиком и весит
 * ноль байт.
 *
 * Громкость — указаний (`cueVolume`): это тот же род звука, что вход в канал и
 * оклик сообщения. В режиме стримера молчит, как и они.
 */
export type Ringtone = "incoming" | "outgoing";

/** Ноты фразы: частота, сдвиг от начала фразы и длительность, секунды. */
const PHRASES: Record<Ringtone, { every: number; level: number; notes: [number, number, number][] }> = {
  // Вызванному — восходящее арпеджио ми мажора и отголосок сверху.
  incoming: {
    every: 2.4,
    level: 0.22,
    notes: [
      [659.25, 0, 0.5],
      [830.61, 0.13, 0.5],
      [987.77, 0.26, 0.5],
      [1318.51, 0.39, 0.9],
      [987.77, 0.78, 0.4],
      [1318.51, 0.91, 1.1],
    ],
  },
  // Звонящему — мягкий двойной гудок, реже и тише: он ждёт, а не зовёт.
  outgoing: {
    every: 3,
    level: 0.12,
    notes: [
      [587.33, 0, 0.9],
      [880, 0.22, 1.2],
    ],
  },
};

/**
 * Начать мелодию; возвращает «остановить». Фразы ставятся в очередь Web Audio
 * таймером на шаг вперёд — так повтор не дрожит, даже когда вкладка в фоне
 * и таймеры редеют.
 */
export function startRingtone(kind: Ringtone): () => void {
  const volume = cueVolume();
  if (volume <= 0 || useStreamer.getState().on) return () => {};

  const context = audioContext();
  void context.resume().catch(() => {});
  const { every, level, notes } = PHRASES[kind];
  const master = context.createGain();
  master.gain.value = level * volume;
  master.connect(context.destination);

  let next = context.currentTime + 0.05;
  const phrase = (at: number) => {
    for (const [frequency, offset, length] of notes) {
      const start = at + offset;
      const gain = context.createGain();
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(1, start + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0008, start + length);
      gain.connect(master);
      // Чистый тон и тихая октава сверху — колокольчик, а не писк.
      for (const [multiple, share] of [
        [1, 1],
        [2, 0.18],
      ] as const) {
        const tone = context.createOscillator();
        const part = context.createGain();
        part.gain.value = share;
        tone.frequency.value = frequency * multiple;
        tone.connect(part).connect(gain);
        tone.start(start);
        tone.stop(start + length + 0.05);
        tone.onended = () => part.disconnect();
      }
      window.setTimeout(() => gain.disconnect(), (start - context.currentTime + length + 0.2) * 1000);
    }
  };

  const schedule = () => {
    // Очередь на шаг вперёд: следующая фраза уже лежит, когда играет текущая.
    while (next < context.currentTime + every) {
      phrase(next);
      next += every;
    }
  };
  schedule();
  const timer = window.setInterval(schedule, 500);

  return () => {
    window.clearInterval(timer);
    // Не обрывать щелчком — короткое затухание.
    const now = context.currentTime;
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(master.gain.value, now);
    master.gain.linearRampToValueAtTime(0, now + 0.08);
    window.setTimeout(() => master.disconnect(), 150);
  };
}
